// btw-front/src/components/admin/dashboard/PerformanceTab.jsx
// [파트너 실적] 탭 — 파트너별 집계
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StatCard } from "../../ui/StatCard";
import { EmptyState } from "../../ui/EmptyState";
import * as adminPerformance from "../../../api/adminPerformance";
import { useQueryState, parseSort, formatSort } from "../../../lib/useQueryState";
import { formatKRW, SortTh } from "./shared";
import { NUMERIC_SORT_KEYS, sortPerformanceRows } from "./performanceSort";

export default function PerformanceTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState({ kpi: { partners: 0, active: 0, poTotal: 0 }, partners: [] });
  const [loading, setLoading] = useState(true);
  // 정렬 상태 — URL 에 "key.dir" 로 보관해 파트너 실적 상세에서 돌아와도 유지
  const [sortRaw, setSortRaw] = useQueryState("sort");
  const sort = useMemo(() => parseSort(sortRaw), [sortRaw]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminPerformance
      .get()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setData({ kpi: { partners: 0, active: 0, poTotal: 0 }, partners: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 같은 컬럼 재클릭 시 방향 전환, 다른 컬럼은 컬럼별 시작 방향으로
  const toggleSort = (key) =>
    setSortRaw((raw) => {
      const s = parseSort(raw);
      if (s && s.key === key) return formatSort({ key, dir: s.dir === "asc" ? "desc" : "asc" });
      return formatSort({ key, dir: NUMERIC_SORT_KEYS.includes(key) ? "desc" : "asc" });
    });
  const sortedPartners = useMemo(
    () => sortPerformanceRows(data.partners, sort),
    [data.partners, sort],
  );

  return (
    <div>
      <section className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <StatCard label={t("admin.performance.kpi.partners")} value={data.kpi.partners} />
        <StatCard label={t("admin.performance.kpi.active")} value={data.kpi.active} />
        <StatCard
          accent
          label={t("admin.performance.kpi.poTotal")}
          value={`${formatKRW(data.kpi.poTotal)} ${t("common.unitKrw")}`}
        />
      </section>

      <section className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {loading ? (
          <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>
        ) : data.partners.length === 0 ? (
          <EmptyState title={t("admin.empty")} />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <SortTh
                  sort={sort}
                  onToggle={toggleSort}
                  sortKey="partner"
                  label={t("admin.performance.table.partner")}
                />
                <SortTh
                  sort={sort}
                  onToggle={toggleSort}
                  sortKey="total"
                  label={t("admin.performance.table.totalDeals")}
                  align="right"
                />
                <SortTh
                  sort={sort}
                  onToggle={toggleSort}
                  sortKey="inProgress"
                  label={t("admin.performance.table.inProgress")}
                  align="right"
                />
                <SortTh
                  sort={sort}
                  onToggle={toggleSort}
                  sortKey="adoption"
                  label={t("admin.performance.table.adoption")}
                  align="right"
                />
                <SortTh
                  sort={sort}
                  onToggle={toggleSort}
                  sortKey="lost"
                  label={t("admin.performance.table.lost")}
                  align="right"
                />
                <SortTh
                  sort={sort}
                  onToggle={toggleSort}
                  sortKey="poTotal"
                  label={t("admin.performance.table.poTotal")}
                  align="right"
                />
                <th className="px-4 py-2.5 text-left font-medium">
                  {t("admin.performance.table.lastActivity")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sortedPartners.map((s) => (
                <tr
                  key={s.partner.id}
                  onClick={() => navigate(`/partners/performance?partnerId=${s.partner.id}`)}
                  className="cursor-pointer transition hover:bg-slate-50"
                >
                  <td className="px-4 py-2.5 font-medium text-slate-900">
                    {s.partner.companyName}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">{s.total}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">
                    {s.inProgress}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">
                    {s.adoption}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{s.lost}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">
                    {formatKRW(s.poTotal)} {t("common.unitKrw")}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500">
                    {s.lastActivityAt ? s.lastActivityAt.slice(0, 10) : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
